import { useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageTitle from "@/components/base/PageTitle";

const subjects = ["Diamond Sourcing Inquiry", "Custom Order Request", "Consultation Booking", "General Question", "Partnership Opportunity"];

const contactInfo = [
  { icon: "ri-mail-line", label: "EMAIL", value: "hello@zayan-atelier.com", href: "mailto://hello@zayan-atelier.com" },
  { icon: "ri-phone-line", label: "PHONE", value: "+1 (915) 262-5727", href: "https://" },
  { icon: "ri-map-pin-line", label: "BY APPOINTMENT", value: "Serving clients worldwide through private consultations.", href: null },
  { icon: "ri-instagram-line", label: "INSTAGRAM", value: "@Zayan_atelier", href: "https://instagram.com/Zayan_atelier" },
];

const FORM_ACTION = "https://readdy.ai/api/form/d83sc2u6qilughrrc2h0";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const leftRef = useScrollReveal<HTMLDivElement>();
  const formRef = useScrollReveal<HTMLDivElement>();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        params.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: params.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || `Submission failed (${response.status})`);
      }
    } catch (err) {
      setSubmitError(
        `Failed to send: ${err instanceof Error ? err.message : "Please try again."}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zayan-bg flex flex-col">
      <PageTitle title="Contact - Zayan Atelier" />
      <Navbar />

      <section className="pt-[130px] md:pt-[170px] pb-16 px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 max-w-7xl mx-auto">
          {/* Left Side - Contact Info + Image */}
          <div className="flex flex-col gap-8 reveal" ref={leftRef}>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">CONTACT ZAYAN ATELIER</span>
              <div className="h-px w-12 bg-zayan-gold" />
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl text-zayan-gold leading-tight">
              Contact<br />Zayan Atelier
            </h2>
            <div className="h-px w-16 bg-zayan-gold" />
            <p className="text-zayan-textMuted text-sm leading-relaxed max-w-md">
              Personalized consultations and sourcing inquiries are available by appointment.
            </p>

            <div className="flex flex-col gap-5">
              {contactInfo.map((info, i) => (
                <div key={i} className={`flex items-start gap-4`}>
                  <div className="w-12 h-12 flex items-center justify-center rounded-full border border-zayan-gold/40 shrink-0">
                    <i className={`${info.icon} text-zayan-gold text-lg`} />
                  </div>
                  <div>
                    <p className="text-zayan-gold text-xs tracking-[0.15em] font-sans mb-0.5">{info.label}</p>
                    {info.href ? (
                      <a href={info.href} target={info.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-zayan-textMuted text-sm hover:text-zayan-gold transition-colors">
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-zayan-textMuted text-sm">{info.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="w-full max-w-md overflow-hidden rounded-lg border border-zayan-border mt-4">
              <img
                src="https://readdy.ai/api/search-image?query=luxury%20round%20brilliant%20cut%20diamond%20held%20by%20golden%20metal%20tweezers%20against%20dark%20black%20background%2C%20dramatic%20lighting%2C%20sparkling%20facets%2C%20extreme%20close%20up%2C%20professional%20jewelry%20photography%2C%20ultra%20high%20detail&width=500&height=400&seq=contact-img&orientation=landscape"
                alt="Diamond in tweezers"
                className="w-full h-64 object-cover object-top"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.onerror = null;
                  target.src = "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=500&h=400&fit=crop&q=80";
                }}
              />
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-zayan-bgCard border border-zayan-border p-6 lg:p-10 reveal" ref={formRef}>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">SEND US A MESSAGE</span>
              <div className="h-px flex-1 bg-zayan-border" />
            </div>
            <form
              id="contact-form"
              action={FORM_ACTION}
              method="POST"
              data-readdy-form="contact"
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-zayan-text text-xs tracking-wider">Your Name *</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                      <i className="ri-user-line text-zayan-gold/60 text-sm" />
                    </div>
                    <input name="name" type="text" required placeholder="Full name" className="w-full bg-zayan-bg border border-zayan-border text-zayan-text text-sm pl-10 pr-4 py-3 outline-none focus:border-zayan-gold transition-colors" />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-zayan-text text-xs tracking-wider">Email Address *</label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                      <i className="ri-mail-line text-zayan-gold/60 text-sm" />
                    </div>
                    <input name="email" type="email" required placeholder="Your email address" className="w-full bg-zayan-bg border border-zayan-border text-zayan-text text-sm pl-10 pr-4 py-3 outline-none focus:border-zayan-gold transition-colors" />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-zayan-text text-xs tracking-wider">Subject *</label>
                <div className="relative">
                  <select name="subject" required className="w-full bg-zayan-bg border border-zayan-border text-zayan-textMuted text-sm px-4 py-3 pr-10 outline-none focus:border-zayan-gold transition-colors appearance-none cursor-pointer">
                    <option value="">Select an option</option>
                    {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-zayan-gold text-lg" />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-zayan-text text-xs tracking-wider">Message *</label>
                <textarea name="message" rows={5} maxLength={500} required placeholder="How can we assist you?" className="bg-zayan-bg border border-zayan-border text-zayan-text text-sm px-4 py-3 outline-none focus:border-zayan-gold transition-colors resize-none" />
                <span className="text-zayan-textDarker text-xs">Max 500 characters</span>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-zayan-gold text-zayan-bg py-3 text-xs tracking-[0.15em] font-sans hover:bg-zayan-goldLight transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "SENDING..." : "SEND MESSAGE"}
              </button>
              {submitted && (
                <p className="text-zayan-gold text-sm text-center gold-shimmer flex items-center justify-center gap-2">
                  <i className="ri-check-line text-zayan-gold" />
                  Thank you! Your message has been sent successfully.
                </p>
              )}
              {submitError && (
                <p className="text-red-400 text-sm text-center">{submitError}</p>
              )}
              <div className="flex items-center justify-center gap-2 text-zayan-textMuted text-xs mt-2">
                <i className="ri-shield-check-line text-zayan-gold" />
                <span>Your information is kept confidential and used solely for client communication.</span>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Schedule Consultation Banner */}
      <section className="border-t border-zayan-border bg-zayan-bgCard px-6 lg:px-16 py-12">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <img
              src="https://static.readdy.ai/image/f050cadc4e04390e0c8f3be3f5e83a4a/754daf6fa5a838036e698ab0e430b19f.png"
              alt="Zayan Atelier"
              className="h-20 w-auto opacity-70"
            />
            <div className="h-12 w-px bg-zayan-border" />
            <div>
              <h3 className="font-serif text-2xl lg:text-3xl text-zayan-gold mb-2">Schedule a Private Consultation</h3>
              <p className="text-zayan-textMuted text-sm">Book a one-on-one consultation to discuss your preferences and begin your personalized diamond sourcing journey.</p>
            </div>
          </div>
          <Link
            to="/request-a-diamond"
            className="shrink-0 border border-zayan-gold text-zayan-gold px-8 py-3 text-xs tracking-[0.15em] hover:bg-zayan-gold hover:text-zayan-bg transition-colors whitespace-nowrap"
          >
            SCHEDULE CONSULTATION
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}